import type { MutationUpdaterFn } from 'apollo-client';

import type { Comment } from '../src/useAllCommentsData';

export const findCommentInData = (
	commentData: Comment[],
	commentId: string,
): Comment | undefined => {
	// Search in retreived comments and their replies
	for (const comment of commentData) {
		// Check if this is the comment we're looking for
		if (comment?.id === commentId) {
			return comment;
		}

		// Check if it's in the replies
		if (comment?.replies) {
			const foundReply = comment.replies.find((reply) => reply?.id === commentId);
			if (foundReply) {
				return foundReply as Comment;
			}
		}
	}

	// else comment not found
	return undefined;
};

// To avoid Sonar errors about nested ternaries, as the false functions (the legacy code paths for
// cache updates) often contain ternaries
export const selectUpdateFunction = <TResultType>(
	condition: boolean,
	trueFn?: MutationUpdaterFn<TResultType>,
	falseFn?: MutationUpdaterFn<TResultType>,
) => (condition ? trueFn : falseFn);

//
/**
 * Finds the root (top-level) comment that contains a reply with the given ID
 * Note that the reply arrays are actually flat arrays of Comment objects - nested comments are not
 * actually nested in the GQL data schema but stored flat in the root parent's replies array
 * with the parentId field that indicates which comment they really reply to (we build the actual
 * nested structure elsewhere in the FE). So this function finds where you should add a reply -
 * even a nested reply - in the schema (or undefined if the parent is not found in the data).
 * @param commentData - Array of root level comments to search through
 * @param replyParentId - The parent ID of the reply to find
 * @returns The root comment that contains the reply, or undefined if not found
 */
export const findRootCommentForReply = (
	commentData: Comment[],
	replyParentId: string,
): Comment | undefined => {
	// Search through each root level comment's replies

	for (const comment of commentData) {
		// Return this comment if it is the parent of the reply
		if (comment?.id === replyParentId) {
			return comment;
		}

		// Else check if this comment has replies and if any of them have the matching parentId
		if (comment?.replies) {
			const foundReply = comment.replies.find((reply) => reply?.id === replyParentId);
			if (foundReply) {
				return comment;
			}
		}
	}

	// Reply parent not found
	return undefined;
};
